<script setup>
import { defineEmits, computed, h } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

// [TODO] Instead of converting the values to their reprentation when building the tableData
// We should do the change in the cell
import { messageStamp, dynamicTime } from 'shared/helpers/timeHelper';

// components
import Table from 'dashboard/components/table/Table.vue';
// REMOVE import PaginationFooter (and Pagination); only UI lives in parent
// (Do not import PaginationFooter)
import UserAvatarWithName from 'dashboard/components/widgets/UserAvatarWithName.vue';
import ConversationCell from './ConversationCell.vue';

// constants
import { CSAT_RATINGS } from 'shared/constants/messages';

import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';

const { pageIndex } = defineProps({
  pageIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();
// Expect csatResponses & metrics from Vuex or parent context (no mock here)
const csatResponses = useMapGetter('csat/getCSATResponses');
const metrics = useMapGetter('csat/getMetrics');
const tableData = computed(() => {
  if (!csatResponses.value) return [];
  return csatResponses.value.map(response => ({
    contact: response.contact,
    assignedAgent: response.assigned_agent,
    rating: response.rating,
    feedbackText: response.feedback_message || '---',
    conversationId: response.conversation_id,
    createdAgo: dynamicTime(response.created_at),
    createdAt: messageStamp(response.created_at, 'LLL d yyyy, h:mm a'),
  }));
});

const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue() ? '' : 'text-slate-300 dark:text-slate-700',
    },
    cellProps.getValue() ? cellProps.getValue() : '---'
  );

const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor('contact', {
    header: t('CSAT_REPORTS.TABLE.HEADER.CONTACT_NAME'),
    width: 200,
    cell: cellProps => {
      const { contact } = cellProps.row.original;
      if (contact) {
        return h(UserAvatarWithName, { user: contact });
      }
      return '--';
    },
  }),
  columnHelper.accessor('assignedAgent', {
    header: t('CSAT_REPORTS.TABLE.HEADER.AGENT_NAME'),
    width: 200,
    cell: cellProps => {
      const { assignedAgent } = cellProps.row.original;
      if (assignedAgent) {
        return h(UserAvatarWithName, { user: assignedAgent });
      }
      return '--';
    },
  }),
  columnHelper.accessor('rating', {
    header: t('CSAT_REPORTS.TABLE.HEADER.RATING'),
    align: 'center',
    width: 80,
    cell: cellProps => {
      const { rating: giveRating } = cellProps.row.original;
      const [ratingObject = {}] = CSAT_RATINGS.filter(
        rating => rating.value === giveRating
      );

      return h(
        'span',
        {
          class: ratingObject.emoji
            ? 'emoji-response text-lg'
            : 'text-slate-300 dark:text-slate-700',
        },
        ratingObject.emoji || '---'
      );
    },
  }),
  columnHelper.accessor('feedbackText', {
    header: t('CSAT_REPORTS.TABLE.HEADER.FEEDBACK_TEXT'),
    width: 400,
    cell: defaulSpanRender,
  }),
  columnHelper.accessor('conversationId', {
    header: '',
    width: 100,
    cell: cellProps => h(ConversationCell, cellProps),
  }),
];

const paginationParams = computed(() => {
  return {
    pageIndex,
    pageSize: 25,
  };
});

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return metrics.value?.totalResponseCount || 0;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagintaion = updater(paginationParams.value);
    emit('pageChange', newPagintaion.pageIndex);
  },
});

// Add pagination state/computed for PaginationFooter:
const currentPage = computed(() => pageIndex + 1);
// Table returns 25/page, change if you wish!
const itemsPerPage = 25;
const totalItems = computed(() => metrics.value?.totalResponseCount || 0);

// Only emit pageChange! (no pagination UI here)
function onPageChangeFooter(pageNum) {
  emit('pageChange', pageNum - 1);
}
</script>

<template>
  <div class="rounded-xl bg-n-solid-2 px-2 py-3 md:px-6 md:py-5">
    <!-- Mobile: Card layout for <sm -->
    <div class="flex flex-col gap-3 sm:hidden">
      <div
        v-for="item in csatResponses"
        :key="item.id"
        class="p-3 rounded-lg border border-n-solid-3 bg-white dark:bg-n-solid-2 shadow-sm flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <UserAvatarWithName :user="item.contact" />
          <span class="text-xl md:text-2xl">{{ (CSAT_RATINGS.find(r=>r.value===item.rating)||{}).emoji || '—' }}</span>
        </div>
        <div class="flex items-center gap-3 text-xs text-n-slate-10 pb-1">
          <span><b>{{ $t('CSAT_REPORTS.TABLE.HEADER.AGENT_NAME') }}:</b></span>
          <UserAvatarWithName :user="item.assigned_agent" :text-class="'text-xs text-n-slate-10'" />
          <span class="ml-auto">{{ dynamicTime(item.created_at) }}</span>
        </div>
        <div class="text-sm text-n-slate-12 px-2 py-1 bg-n-solid-2 rounded" v-if="item.feedback_message">
          {{ item.feedback_message }}
        </div>
        <div class="flex items-center gap-2 text-xs text-n-slate-10 pt-1">
          <span><b>ID:</b> {{ item.conversation_id }}</span>
          <span>|</span>
          <span><b>{{ $t('CSAT_REPORTS.TABLE.HEADER.RATING') }}:</b> {{ item.rating }}</span>
        </div>
      </div>
      <div v-show="!csatResponses.length" class="h-24 flex items-center justify-center text-n-slate-12 text-sm">
        {{ $t('CSAT_REPORTS.NO_RECORDS') }}
      </div>
    </div>
    <div class="hidden sm:block">
      <Table :table="table" :minimal="true" class="max-h-[calc(100vh-21.875rem)]" />
      <div v-show="!tableData.length" class="h-48 flex items-center justify-center text-n-slate-12 text-sm">
        {{ $t('CSAT_REPORTS.NO_RECORDS') }}
      </div>
    </div>
  </div>
</template>
